import React, { useEffect, useState } from 'react';
import Button from '../../../../../../components/controls/Button';
import { createInputFieldId, FormGroup, From, InputField } from '../../../../../../components/controls/Form';
import { TextParagraph } from '../../../../../../components/controls/Typography/TextBody';
import BaseModal from '../../../../../../components/modals/BaseModal';
import { Alert } from '../../../../../../components/visual/Alert/Alert';
import { apiPOST } from '../../../../../../utils/networking';
import useAuthUtils from '../../../../../../utils/useAuthUtils';
import useMessages from '../../../../../../utils/useMessages';

/**
 *
 * @param {{
 *  onSuccess?():void;
 * }} props
 * @returns {JSX.Element} - The rendered DelegateModal component.
 */
export const DelegateModalAddInvoker = (props) => {
  const { onSuccess } = props;
  const [isOpened, setIsOpened] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const messages = useMessages();
  const auth = useAuthUtils();

  const customInputIdForFirstName = createInputFieldId()

  const getContent = () => {
    const handleFormSubmit = async () => {
      setIsBusy(true);
      setErrorMessage('');
      try {
        const payload = {
          firstName,
          lastName,
          emailAddress,
          enabled: true,
        };
        if (payload.emailAddress === auth.account?.email.emailAddress) {
          throw new Error('You cannot add yourself as a delegate.');
        }
        const resp = await apiPOST('/recommendation/rest/evaluatorDelegate', payload);
        if (/^20/.test(resp.status)) {
          onSuccess?.();
          onModalClose();
          messages.clear().notifyGreen('Delegate added successfully.');
        } else {
          throw new Error('An error occurred while sending the invite. Please try again.');
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(
          error?.body?.message ?? error?.message ?? 'An error occurred while sending the invite. Please try again.'
        );
      } finally {
        setIsBusy(false);
      }
    }

    // ensure the right input field is focused when the modal is opened
    setTimeout(() => {
      document.getElementById(customInputIdForFirstName)?.focus();
    }, 100);

    return <>
      <From
        noAutocomplete
        onSubmit={handleFormSubmit}
        formAttr={{
          title: 'Add a Delegate',
        }}
        noSpacings
        errors={<Alert show={!!errorMessage} message={errorMessage} type={'red'} />}
        buttons={
          <>
            <Button
              primary
              outline
              type='button'
              disabled={isBusy}
              onClick={onModalClose}
            >
              Cancel
            </Button>

            <Button
              primary
              submit
              disabled={isBusy}
              isBusy={isBusy}
              type='button'
            >
              Send Invite
            </Button>
          </>
        }
      >

        <TextParagraph className='mb-2'>
          You can delegate individuals to help you manage your evaluations.<br />
          When you add a delegate, they’ll be able to submit evaluations on your behalf,<br />
          view your incoming evaluation requests, and receive any related email notifications.<br />
          Evaluations for some applicants cannot be delegated. You will have to complete these evaluations yourself.<br />
          Additional details are available in the Liaison Letters <a href="https://help.liaisonedu.com/Liaison_Letters_Help_Center/Completing_a_Recommendation/Delegate" rel="noopener noreferrer" target="_blank">Help Center</a>.
        </TextParagraph>

        <FormGroup
          label={'First Name'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          id={customInputIdForFirstName}
          controlRender={({ id }) => <InputField
            title={'First Name'}
            name={'firstName'}
            id={id}
            autoComplete={'new-password'}
            autofocus
            onChange={(n, value) => setFirstName(value)}
            value={firstName}
            limit={50}
            required
          />}
        />

        <FormGroup
          label={'Last Name'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          controlRender={({ id }) => <InputField
            title={'Last Name'}
            name={'lastName'}
            id={id}
            autoComplete={'new-password'}
            onChange={(n, value) => setLastName(value)}
            value={lastName}
            limit={50}
            required
          />}
        />

        <FormGroup
          label={'Email'}
          required
          horizontal
          labelSize={4}
          alignWithControlBaseline
          controlRender={({ id }) => <InputField
            title={'Email'}
            name={'email'}
            inputType={'email'}
            id={id}
            autoComplete={'new-password'}
            onChange={(n, value) => setEmailAddress(value)}
            value={emailAddress}
            limit={255}
            required
          />}
        />

      </From>
    </>
  }

  const onModalClose = () => {
    setIsOpened(false);
    setErrorMessage('');
    setFirstName('');
    setLastName('');
    setEmailAddress('');
  }

  useEffect(() => {
    return () => {
      onModalClose();
    }
  }, []);

  return <>
    <Button primary
      onClick={() => {
        setIsOpened(true);
      }}>
      {'Add a Delegate'}
    </Button>
    <BaseModal
      initialFocus={false}
      show={isOpened}
      isBusy={isBusy}
      title={'Add a Delegate'}
      closeIcon={true}
      body={getContent()}
      onClose={() => {
        onModalClose?.();
      }}
      hideFooter
    />
  </>
}
